import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const TIMEOUT_MS = 120_000; // 2 minutes

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('access_password');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const requireAuth = () => {
  if (!sessionStorage.getItem('access_password')) {
    window.location.reload();
    throw new Error('Not authenticated');
  }
};

const getPasswordHeader = () => {
  const password = sessionStorage.getItem('access_password');
  return password ? { 'X-Access-Password': password } : {};
};

export const sendMessage = async (message, conversationHistory = []) => {
  requireAuth();
  try {
    const response = await axios.post(`${API_BASE_URL}/api/chat`, {
      message,
      conversation_history: conversationHistory
    }, { headers: getPasswordHeader(), timeout: TIMEOUT_MS });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') throw new Error('Request timed out — backend may be unavailable');
    throw new Error(error.response?.data?.detail || 'Failed to communicate with backend');
  }
};

export const resetWorkspace = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/reset`, {}, { headers: getPasswordHeader(), timeout: TIMEOUT_MS });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to reset workspace');
  }
};

export const checkHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/health`, { timeout: 10_000 });
    return response.data;
  } catch (error) {
    throw new Error('Backend is not responding');
  }
};

export const getCurrentModel = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/current-model`, { headers: getPasswordHeader(), timeout: TIMEOUT_MS });
    return response.data;
  } catch (error) {
    throw new Error('Failed to get current model');
  }
};

export const getSketchInfo = async (sketchName) => {
  const response = await axios.get(`${API_BASE_URL}/api/sketch/${sketchName}`,
    { headers: getPasswordHeader(), timeout: 10000 });
  return response.data;
};

export const streamMessage = async (message, conversationHistory, onPlan, onToolCall, onToolResult, onDone, onError, onReview) => {
  requireAuth();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getPasswordHeader() },
      body: JSON.stringify({
        message,
        conversation_history: conversationHistory
      }),
      signal: controller.signal
    });
  } catch (err) {
    clearTimeout(timeoutId);
    onError(err.name === 'AbortError' ? 'Request timed out — backend may be unavailable' : err.message);
    return;
  }

  if (response.status === 401) {
    clearTimeout(timeoutId);
    sessionStorage.removeItem('access_password');
    window.location.reload();
    return;
  }
  if (!response.ok) {
    clearTimeout(timeoutId);
    const err = await response.text();
    onError(err || 'Request failed');
    return;
  }
  clearTimeout(timeoutId);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line in buffer

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const jsonStr = line.slice(6);
      if (!jsonStr) continue;

      try {
        const event = JSON.parse(jsonStr);
        if (event.type === 'plan') {
          onPlan(event.content);
        } else if (event.type === 'tool_call') {
          onToolCall(event.name, event.args);
        } else if (event.type === 'tool_result') {
          onToolResult(event.name, event.success, event.message, {
            model_url: event.model_url,
            step_url: event.step_url,
            parts: event.parts,
            feature_tree: event.feature_tree,
            body_meshes: event.body_meshes,
          });
        } else if (event.type === 'review') {
          if (onReview) onReview(event);
        } else if (event.type === 'done') {
          onDone(event);
        } else if (event.type === 'error') {
          onError(event.message);
        }
      } catch (e) {
        console.warn('Malformed SSE event:', jsonStr, e);
      }
    }
  }
};
